import { gql } from "@apollo/client";
import { CurrencyCode } from "@asmbl/shared/constants";
import { add, Money, subtract, zero } from "@asmbl/shared/money";
import { mapMaybe } from "@asmbl/shared/utils";
import { TableCell, TableRow, Typography } from "@mui/material";
import makeStyles from "../../../../makeStylesV4Compat";
import clsx from "clsx";
import {
  ActualSummarizedRow_compCycleBudget as CompCycleBudget,
  ActualSummarizedRow_compCycleData as CompCycleData,
  ActualSummarizedRow_manager as Manager,
  RecItemType,
  ActualSummarizedRow_reports as Report,
} from "../../../../__generated__/graphql";
import { CompCycleDisplay } from "../../../../components/CompCycle/types";
import { useCurrencies } from "../../../../components/CurrenciesContext";
import { formatBudget } from "../../../../models/Budget";
import {
  calculateRequestsFromReports,
  formatRequest,
  formatVariance,
} from "../../../../models/CompRecommendation";
import { DV_RED, GRAY_4, GRAY_8 } from "../../../../theme";
import { IndentationCells } from "../../Budgets/Table/Connectors";
import { SummarizedNameCell } from "./SummarizedNameCell";

type Props = {
  manager: Manager | null;
  reports: Report[];
  rootBudget: CompCycleBudget | null;
  organizationName: string;
  varianceDisplay: CompCycleDisplay;
  compCycleData: CompCycleData;
  indentation: IndentationCells;
};

type BudgetKey = "bonus" | "actualRecurringBonus";

const useStyles = makeStyles(() => ({
  root: {
    height: "1.5rem",

    "& tr": {
      height: "1.5rem",
    },

    "& td": {
      height: "24px",
      paddingTop: 0,
      paddingBottom: 0,
      backgroundColor: GRAY_8,
    },
  },
  text: {
    color: GRAY_4,
    fontSize: "0.8125rem",
    lineHeight: "1.259375",
    fontWeight: 400,
    letterSpacing: "-0.25px",
  },
  alertText: { color: DV_RED },
}));

function getTopLevelBudget(
  rootBudget: CompCycleBudget | null,
  manager: Manager | null,
  budgetKey: BudgetKey
): Money | null {
  // if this budget is an organization level budget
  if (manager === null && rootBudget && rootBudget[budgetKey]) {
    return rootBudget[budgetKey];
  }

  // if this budget is attached to an employee
  if (manager !== null && manager.compCycleBudget?.[budgetKey]) {
    return manager.compCycleBudget[budgetKey];
  }

  return null;
}

function getReportsBudget(
  defaultCurrencyCode: CurrencyCode,
  reports: Report[],
  budgetKey: BudgetKey
): Money | null {
  const mappedBudget = mapMaybe(
    reports,
    (employee) => employee.compCycleBudget?.[budgetKey]
  );

  if (mappedBudget.length === 0) return null;

  return mappedBudget.reduce(add, zero(defaultCurrencyCode));
}

export function ActualSummarizedRow({
  manager,
  reports,
  rootBudget,
  organizationName,
  varianceDisplay,
  compCycleData,
  indentation,
}: Props): JSX.Element {
  const classes = useStyles();
  const { defaultCurrencyCode } = useCurrencies();

  const bonus = {
    budgetData: {
      top: getTopLevelBudget(rootBudget, manager, "bonus"),
      current: getReportsBudget(defaultCurrencyCode, reports, "bonus"),
    },
    requests: calculateRequestsFromReports(reports, RecItemType.MERIT_BONUS),
  };

  const actualRecurring = calculateRequestsFromReports(
    reports,
    RecItemType.ACTUAL_RECURRING_BONUS
  );

  const actualCompanyRecurring = calculateRequestsFromReports(
    reports,
    RecItemType.ACTUAL_RECURRING_BONUS_COMPANY
  );

  const actualCustomRecurring = calculateRequestsFromReports(
    reports,
    RecItemType.ACTUAL_RECURRING_BONUS_CUSTOM
  );

  let actualRecurringTotalRequests = null;
  if (actualRecurring) {
    actualRecurringTotalRequests = actualRecurring;
  }
  if (actualCompanyRecurring) {
    actualRecurringTotalRequests = actualRecurringTotalRequests
      ? add(actualRecurringTotalRequests, actualCompanyRecurring)
      : actualCompanyRecurring;
  }
  if (actualCustomRecurring) {
    actualRecurringTotalRequests = actualRecurringTotalRequests
      ? add(actualRecurringTotalRequests, actualCustomRecurring)
      : actualCustomRecurring;
  }

  const actual = {
    budgetData: {
      top: getTopLevelBudget(rootBudget, manager, "actualRecurringBonus"),
      current: getReportsBudget(
        defaultCurrencyCode,
        reports,
        "actualRecurringBonus"
      ),
    },
    requests: actualRecurringTotalRequests,
  };

  return (
    <TableRow className={classes.root}>
      {/* Name & title cell + direct & indirect cells */}
      <SummarizedNameCell
        subjectName={manager ? manager.displayName : organizationName}
        indentation={indentation}
      />

      {/* Direct & indirect cells */}
      <TableCell colSpan={1} />
      <TableCell colSpan={1} />
      {[
        ...(compCycleData.allowBonus ? [bonus] : []),
        ...(compCycleData.allowActualRecurringBonus ? [actual] : []),
      ].map(({ requests, budgetData }) => {
        const budget = budgetData.top
          ? subtract(
              budgetData.top,
              budgetData.current ?? zero(defaultCurrencyCode)
            )
          : null;
        const variance = budget && requests ? subtract(requests, budget) : null;

        return (
          <>
            {/* budget */}
            <TableCell align="right">
              <Typography className={classes.text}>
                {formatBudget(budget)}
              </Typography>
            </TableCell>

            {/* request */}
            <TableCell align="right">
              <Typography className={classes.text}>
                {formatRequest(requests)}
              </Typography>
            </TableCell>

            <TableCell align="right">
              <Typography
                className={clsx(classes.text, {
                  [classes.alertText]: variance && variance.value > 0,
                })}
              >
                {formatVariance(varianceDisplay, budget, variance)}
              </Typography>
            </TableCell>
          </>
        );
      })}
    </TableRow>
  );
}

ActualSummarizedRow.fragments = {
  compCycleBudget: gql`
    fragment ActualSummarizedRow_compCycleBudget on CompCycleBudget {
      compCycleId
      employeeId
      bonus
      actualRecurringBonus
    }
  `,
  manager: gql`
    fragment ActualSummarizedRow_manager on Employee {
      id
      displayName

      compCycleBudget(compCycleId: $compCycleId) {
        compCycleId
        employeeId
        bonus
        actualRecurringBonus
      }
    }
  `,
  reports: gql`
    fragment ActualSummarizedRow_reports on Employee {
      id
      displayName

      compRecommendation(compCycleId: $compCycleId) {
        subjectId
        compCycleId
        reviewStatus
        latestSubmittedItems {
          id
          submittedAt
          recommendationType
          annualCashEquivalent(currencyCode: $currencyCode)
        }
      }

      compCycleBudget(compCycleId: $compCycleId) {
        compCycleId
        employeeId
        bonus
        actualRecurringBonus
      }
    }
  `,
  compCycleData: gql`
    fragment ActualSummarizedRow_compCycleData on CompCycle {
      id
      allowBonus
      allowActualRecurringBonus
    }
  `,
};
